import React, { useEffect, useState } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { getFirestore, doc, getDoc } from 'firebase/firestore';
import { useTranslation } from 'react-i18next';
import { FaLock, FaExclamationTriangle, FaClock, FaUserShield } from 'react-icons/fa';

const UserBlocked: React.FC = () => {
  const { currentUser } = useAuth();
  const { t } = useTranslation();
  const [userData, setUserData] = useState<any>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchUserData = async () => {
      if (!currentUser) return;
      
      try {
        const db = getFirestore();
        const userDoc = await getDoc(doc(db, 'users', currentUser.uid));
        
        if (userDoc.exists()) {
          setUserData(userDoc.data());
        }
      } catch (error) {
        console.error('Error fetching user data:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchUserData();
  }, [currentUser]);

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-red-50 via-white to-red-100 flex items-center justify-center">
        <div className="animate-spin rounded-full h-16 w-16 border-b-4 border-red-600"></div>
      </div>
    );
  }

  const formatDate = (dateString: string) => {
    if (!dateString) return 'N/A';
    try {
      const date = new Date(dateString);
      return date.toLocaleString('es-ES', {
        year: 'numeric',
        month: 'long',
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit'
      });
    } catch {
      return 'N/A';
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-red-50 via-white to-red-100 flex items-center justify-center p-4">
      <div className="max-w-2xl w-full bg-white rounded-3xl shadow-2xl p-8 border border-red-200">
        {/* Header */}
        <div className="text-center mb-8">
          <div className="w-24 h-24 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4">
            <FaLock className="text-red-600 text-3xl" />
          </div>
          <h1 className="text-3xl font-bold text-gray-900 mb-2">
            {t('userBlocked.title', 'Cuenta Bloqueada')}
          </h1>
          <p className="text-gray-600 text-lg">
            {t('userBlocked.subtitle', 'Su cuenta ha sido suspendida temporalmente')}
          </p>
        </div>

        {/* Información del bloqueo */}
        <div className="bg-red-50 rounded-2xl p-6 mb-6 border border-red-200">
          <div className="flex items-start gap-4">
            <FaExclamationTriangle className="text-red-600 text-2xl mt-1 flex-shrink-0" />
            <div className="flex-1">
              <h3 className="text-lg font-semibold text-red-800 mb-2">
                {t('userBlocked.blockReason', 'Motivo del bloqueo')}
              </h3>
              <p className="text-red-700 mb-4">
                {userData?.blockReason || userData?.blockReasonDetails || 
                 t('userBlocked.noReasonProvided', 'No se proporcionó un motivo específico')}
              </p>
            </div>
          </div>
        </div>

        {/* Detalles del bloqueo */}
        <div className="space-y-4 mb-8">
          <div className="flex items-center gap-3 p-4 bg-gray-50 rounded-xl">
            <FaClock className="text-gray-600 text-xl" />
            <div>
              <p className="text-sm text-gray-500 font-medium">
                {t('userBlocked.blockedAt', 'Fecha de bloqueo')}
              </p>
              <p className="text-gray-900 font-semibold">
                {formatDate(userData?.blockedAt)}
              </p>
            </div>
          </div>

          <div className="flex items-center gap-3 p-4 bg-gray-50 rounded-xl">
            <FaUserShield className="text-gray-600 text-xl" />
            <div>
              <p className="text-sm text-gray-500 font-medium">
                {t('userBlocked.blockedBy', 'Bloqueado por')}
              </p>
              <p className="text-gray-900 font-semibold">
                {t('userBlocked.administrator', 'Administrador del sistema')}
              </p>
            </div>
          </div>
        </div>

        {/* Información de contacto */}
        <div className="bg-blue-50 rounded-2xl p-6 border border-blue-200">
          <h3 className="text-lg font-semibold text-blue-800 mb-3">
            {t('userBlocked.needHelp', '¿Necesita ayuda?')}
          </h3>
          <p className="text-blue-700 mb-4">
            {t('userBlocked.contactSupport', 'Si cree que esto es un error o desea apelar la decisión, por favor contacte al soporte técnico.')}
          </p>
          <div className="space-y-2 text-sm text-blue-600">
            <p>📧 Email: support@brix.com</p>
            <p>📞 Teléfono: +1 (555) 123-4567</p>
            <p>🕒 Horario: Lunes a Viernes, 9:00 AM - 6:00 PM</p>
          </div>
        </div>

        {/* Footer */}
        <div className="text-center mt-8 pt-6 border-t border-gray-200">
          <p className="text-sm text-gray-500">
            {t('userBlocked.thankYou', 'Gracias por su comprensión')}
          </p>
          <p className="text-xs text-gray-400 mt-1">
            Brix - Sistema de Gestión de Trabajadores
          </p>
        </div>
      </div>
    </div>
  );
};

export default UserBlocked; 